import {
  Card,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  MoreVertRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RRLink, NavLink } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import {
  ApprovalType,
  getApprovalTypesMasterDataAsync
} from './approvalTypeSlice';

interface ApprovalTypeParamProps extends PaginationProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1, 1, 1, 2),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    }
  })
);

export default function ApprovalTypeView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const sortOptions = [
    { value: 'id', text: t('approvalTypeView.sortOptions.createdDate') },
    { value: 'name', text: t('approvalTypeView.sortOptions.name') }
  ];

  const { masterData: approvalTypes, count } = useSelector(
    (state: RootState) => state.approvalTypes
  );

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedApprovalType, setSelectedApprovalType] =
    useState<ApprovalType | null>(null);
  const [approvalTypeParams, setApprovalTypeParams] =
    useState<ApprovalTypeParamProps>({
      page: 0,
      limit: 30,
      sortBy: 'name',
      orderBy: ORDER.asc
    });

  useEffect(() => {
    loadingApprovalType(approvalTypeParams);
  }, []);

  function loadingApprovalType(params: ApprovalTypeParamProps) {
    dispatch(getApprovalTypesMasterDataAsync(params));
    setApprovalTypeParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingApprovalType({
      ...approvalTypeParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, approvalType?: ApprovalType) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && approvalType) {
        setSelectedApprovalType(approvalType);
      }
      setOpenOption(open);
    };
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText primary={selectedApprovalType?.name} />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem
              component={NavLink}
              to={`approval-types/${selectedApprovalType?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('approvalTypeView.drawer.editApprovalType')} />
            </ListItem>
            <ListItem button onClick={() => {}} disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('approvalTypeView.drawer.deleteApprovalType')}
                secondary={t('approvalTypeView.drawer.functionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{
          masterDataName: t('approvalTypeView.breadcrumb.approvalType'),
          masterDataRef: 'approval-types'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('approvalTypeView.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('approvalTypeView.toolbar.name') as string}
        params={approvalTypeParams}
        sortOptions={sortOptions}
        loadingElement={loadingApprovalType}
      />
      {approvalTypes.map((approvalType) => {
        return (
          <Card className={classes.card} key={approvalType.id}>
            <div className={classes.cardContainer}>
              <Typography>{approvalType.name}</Typography>
            </div>
            <div>
              <IconButton
                size="small"
                onClick={toggleDrawer(true, approvalType)}
              >
                <MoreVertRounded />
              </IconButton>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / approvalTypeParams.limit)}
          shape="rounded"
          page={approvalTypeParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`approval-types/create`}
          variant="extended"
          color="primary"
          aria-label={t('approvalTypeView.fab.ariaLabel') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('approvalTypeView.fab.newApprovalType')}
        </Fab>
      </Zoom>
    </div>
  );
}
